import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DropzoneArea } from 'mui-file-dropzone';
import { Alert, Button, FormControl, Link } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormProps, getValidationProps } from 'util/Form';
import Table from 'components/shared/Table';
import { TableColumn } from 'store/types/Table';
import FileInfo from 'store/types/FileInfo';
import { getFormattedFileSize } from 'util/Format';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import ApplicationsService from 'services/api/ApplicationsService';
import { useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { defaultSnackbarErrorProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';
import styles from './FileQuestionFormItem.module.scss';

interface FileQuestionFormItemProps extends FormProps {
  fieldName: string;
  fileRequired?: boolean;
  files?: FileInfo[];
  applicationId?: string;
  showTable?: boolean;
}

const MAX_FILE_SIZE = 5000000;
const MAX_FILES_COUNT = 5;

const FileQuestionFormItem: React.FunctionComponent<FileQuestionFormItemProps> = ({
  fieldName,
  fileRequired,
  files = [],
  required,
  disabled,
  applicationId = '',
  showTable = true,
}) => {
  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext();
  const [list, setList] = useState<FileInfo[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>();
  const skipFirst = React.useRef(true);
  const { applicationId: id = applicationId } = useParams<{ applicationId: string }>();
  const answerFiles = useWatch({ control, name: fieldName });
  const { error } = getValidationProps(`files.${fieldName}`, errors);

  useEffect(() => {
    if (files?.length) {
      setValue(fieldName, files);
      setList(files);
    }
  }, [files, fieldName, setValue, trigger]);

  const handleDeleteButtonClick = useCallback(
    (record: FileInfo) => () => {
      const value = getValues()[fieldName];
      const newValue = value.filter((file: FileInfo) => file.id !== record.id);

      setValue(fieldName, newValue);
      setList(newValue);
      void trigger(fieldName);
    },
    [fieldName, getValues, setValue, trigger]
  );

  const handleFileLinkClick = useCallback(
    (record: FileInfo) => () => {
      setLoading(true);
      ApplicationsService.getApplicationFile(id, record)
        .then(() => {
          setLoading(false);
        })
        .catch((error: string) => {
          enqueueSnackbar(error, defaultSnackbarErrorProps);
          setLoading(false);
        });
    },
    [id, enqueueSnackbar]
  );

  const columns: Array<TableColumn<FileInfo>> = useMemo(() => {
    if (!showTable) return [];

    const result: Array<TableColumn<FileInfo>> = [
      {
        dataIndex: 'name',
        label: 'File Name',
        render: (name: string, record: FileInfo) => (
          <Link onClick={handleFileLinkClick(record)} className={commonStyles.link}>
            {name}
          </Link>
        ),
      },
      {
        dataIndex: 'size',
        label: 'Size',
        render: (size: number) => getFormattedFileSize(size),
      },
    ];

    !disabled &&
      result.push({
        label: 'Action',
        render: (_, record) => (
          <Button
            size={'small'}
            color={'primary'}
            variant={'outlined'}
            className={commonStyles.dangerButtonOutlined}
            onClick={handleDeleteButtonClick(record)}
          >
            <DeleteOutlined className={styles.icon} />
          </Button>
        ),
      });

    return result;
  }, [disabled, handleDeleteButtonClick, handleFileLinkClick, showTable]);

  const validateFilesField = useCallback(
    (value: File[]) => {
      return (required || fileRequired) && !answerFiles?.length && !list?.length ? !!value?.length : true;
    },
    [answerFiles?.length, fileRequired, list?.length, required]
  );

  return (
    <>
      <FormControl required={fileRequired} error={error} className={styles.formControl}>
        <Controller
          name={`files.${fieldName}`}
          control={control}
          defaultValue={[]}
          rules={{
            validate: validateFilesField,
          }}
          render={({ field }) => (
            <>
              <DropzoneArea
                maxFileSize={MAX_FILE_SIZE}
                filesLimit={MAX_FILES_COUNT}
                fileObjects={field.value ?? []}
                onChange={(newFileObjects) => {
                  // DropzoneArea calls onChange once on mount with current files (often [])
                  if (skipFirst.current) {
                    skipFirst.current = false;
                    return; // ignore the mount-time call
                  }
                  field.onChange(newFileObjects);
                }}
                onDelete={field.onChange}
                showAlerts={['error']}
                clearOnUnmount={true}
                showPreviews
                showPreviewsInDropzone={false}
                useChipsForPreview
                previewText=""
                classes={{
                  root: classNames(styles.dropzone, { [styles.disabled]: disabled }),
                  textContainer: styles.dropzoneTextContainer,
                  text: styles.dropzoneText,
                  icon: styles.dropzoneIcon,
                }}
                previewGridClasses={{
                  container: styles.previewContainer,
                  item: styles.previewImageContainer,
                }}
                dropzoneProps={{ disabled, noDrag: false }}
                previewChipProps={{ className: styles.chip, disabled }}
                alertSnackbarProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                }}
              />
            </>
          )}
        />
      </FormControl>

      <Alert severity="error" className={commonStyles.alert} style={{ marginTop: '1rem' }} hidden={!error}>
        Please upload a file.
      </Alert>

      {showTable && <Table columns={columns} list={list} className={styles.sectionTable} loading={loading} />}
    </>
  );
};

export default FileQuestionFormItem;
